/**
 * Browser tracing, the OWNED surface: the app speaks this module's vocabulary and never OTel
 * elsewhere (eslint keeps it so). THE PROCESS LAW (owner-ruled 2026-09-19): one trace per process
 * per unit of work, and THE BROWSER IS A PROCESS. Its unit of work is THE INTERACTION -- a new
 * trace roots at each gesture (a click, a navigation, the page load) and holds everything the
 * gesture caused: the fetches as child spans, the paint after the last response as its end. Each
 * fetch carries the interaction's traceparent; the api roots ITS OWN trace and links back, so Tempo
 * shows the browser's trace and the api's joined by the link, never one inside the other.
 * Performance data is the norm: navigation timing on the page load, bytes on every fetch, the web
 * vitals as events, the route, scope, project and mode on every root.
 *
 * Spans leave through THE DOOR, the api's POST /v1/traces behind the bearer token (the collector
 * has no public address); a 404 from it means tracing is off server-side and the exporter closes.
 * This reverses the 2026-08-01 ruling that the traceparent header was the whole client story.
 */
import {
  context,
  propagation,
  trace,
  SpanKind,
  SpanStatusCode,
  type Attributes,
  type Span,
  type Tracer,
} from '@opentelemetry/api';
import { ExportResultCode, type ExportResult } from '@opentelemetry/core';
import {
  BatchSpanProcessor,
  WebTracerProvider,
  type ReadableSpan,
  type SpanExporter,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-web';
import { JsonTraceSerializer } from '@opentelemetry/otlp-transformer';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

/** Where spans go: the api's door and the bearer that opens it. */
export interface Door {
  url: string;
  token: () => Promise<string | null>;
}

/** The two router events navigation tracing listens to (TanStack Router's shape, structurally). */
export interface NavigationEvents {
  onBeforeNavigate: { toLocation: { pathname: string } };
  onResolved: { toLocation: { pathname: string } };
}

/** Anything that announces navigations the way TanStack Router does. */
export interface Navigations {
  subscribe<K extends keyof NavigationEvents>(eventType: K, fn: (event: NavigationEvents[K]) => void): () => void;
}

const SERVICE = 'polywog-browser';
const SESSION_KEY = 'polywog.session';
/** An accessible name longer than this is a paragraph, not a control. */
const CONTROL_NAME_MAX = 80;
/** Layout shift is unitless and small; it rides the integer grid as ten-thousandths, like PCT. */
const CLS_GRID = 10_000;

let configured = false;
let current: Interaction | null = null;
let pageLoadSpan: Span | null = null;

function tracer(): Tracer {
  return trace.getTracer(SERVICE);
}

/** The next frame after the next paint; outside a browser (tests under jsdom) the next task. */
function afterPaint(callback: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => requestAnimationFrame(callback));
  } else {
    setTimeout(callback, 0);
  }
}

/** The in-memory fallback session id, minted once, for when storage itself is refused. */
let fallbackSessionId: string | null = null;

/** The per-tab session id, minted once; still once per tab where storage is refused. */
function sessionId(): string {
  try {
    const found = sessionStorage.getItem(SESSION_KEY);
    if (found) return found;
    const minted = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, minted);
    return minted;
  } catch {
    fallbackSessionId ??= crypto.randomUUID();
    return fallbackSessionId;
  }
}

/** What every root carries: who, where, and in which mode. */
function rootAttributes(): Attributes {
  const attributes: Attributes = {
    'polywog.process': 'browser',
    'polywog.session_id': sessionId(),
    'polywog.route': window.location.pathname,
    'polywog.mode': document.documentElement.style.colorScheme || 'system',
  };
  const search = new URLSearchParams(window.location.search);
  const scope = search.get('scope');
  const project = search.get('project');
  if (scope) attributes['polywog.scope'] = scope;
  if (project) attributes['polywog.project'] = project;
  return attributes;
}

/**
 * One unit of the browser's work: a root span that stays open while the gesture's fetches are in
 * flight and ends on the frame after the paint that follows the last of them.
 */
export class Interaction {
  readonly span: Span;
  readonly name: string;
  private inflight = 0;
  private settled = false;
  private ended = false;

  constructor(name: string, span: Span) {
    this.name = name;
    this.span = span;
  }

  /** The gesture's own work is done; the span ends after the paint that follows its last fetch. */
  settle(): void {
    this.settled = true;
    this.tryEnd();
  }

  /** A fetch this interaction caused has started. */
  fetchBegan(): void {
    this.inflight += 1;
  }

  /** A fetch this interaction caused has finished. */
  fetchEnded(): void {
    this.inflight -= 1;
    this.tryEnd();
  }

  /** Whether the span is still open. */
  get open(): boolean {
    return !this.ended;
  }

  /** End now, saying why -- a newer gesture superseded this one. */
  endNow(reason: string): void {
    if (this.ended) return;
    this.span.setAttribute('polywog.ended_by', reason);
    this.finish();
  }

  private tryEnd(): void {
    if (this.ended || !this.settled || this.inflight > 0) return;
    afterPaint(() => {
      if (!this.ended && this.inflight === 0) this.finish();
    });
  }

  private finish(): void {
    this.ended = true;
    this.span.end();
    if (current === this) current = null;
  }
}

/**
 * A new interaction: the ROOT of a new trace, never a child of the last one. A gesture that arrives
 * while another is open supersedes it.
 */
export function interaction(name: string, attributes: Attributes = {}): Interaction {
  current?.endNow('superseded');
  const span = tracer().startSpan(`ui.${name}`, { root: true, attributes: { ...rootAttributes(), ...attributes } });
  current = new Interaction(`ui.${name}`, span);
  return current;
}

/** The interaction a fetch belongs to: the open one, or a root of its own for a background refetch. */
function owner(): Interaction {
  if (current?.open) return current;
  // No gesture caused this (react-query refreshing in the background): a trace of its own, which
  // ends when the fetch does.
  const refetch = interaction('refetch');
  refetch.settle();
  return refetch;
}

const HEADER_SETTER = { set: (headers: Headers, key: string, value: string) => headers.set(key, value) };

/**
 * fetch as a CLIENT span under the current interaction: the traceparent injected, the method,
 * path, status and response bytes recorded ("scope is the data budget"), the interaction held open
 * until the response lands.
 */
export async function tracedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  send: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<Response> {
  const holder = owner();
  const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, window.location.origin);
  const method = (init.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const span = tracer().startSpan(
    `HTTP ${method} ${url.pathname}`,
    {
      kind: SpanKind.CLIENT,
      attributes: { 'http.request.method': method, 'url.path': url.pathname, 'url.query': url.search },
    },
    trace.setSpan(context.active(), holder.span),
  );
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init.headers).forEach((v, k) => headers.set(k, v));
  propagation.inject(trace.setSpan(context.active(), span), headers, HEADER_SETTER);
  holder.fetchBegan();
  try {
    const response = await send(input, { ...init, headers });
    span.setAttribute('http.response.status_code', response.status);
    if (!response.ok) span.setStatus({ code: SpanStatusCode.ERROR });
    endWithSize(span, response);
    return response;
  } catch (error) {
    span.setStatus({ code: SpanStatusCode.ERROR, message: error instanceof Error ? error.message : String(error) });
    span.end();
    throw error;
  } finally {
    holder.fetchEnded();
  }
}

/** The response's bytes: the declared length, or the body's length read off a clone when undeclared. */
function endWithSize(span: Span, response: Response): void {
  const declared = response.headers.get('content-length');
  if (declared !== null) {
    span.setAttribute('http.response.body.size', Number(declared));
    span.end();
    return;
  }
  response
    .clone()
    .arrayBuffer()
    .then(
      (body) => {
        span.setAttribute('http.response.body.size', body.byteLength);
        span.end();
      },
      () => span.end(),
    );
}

/** The exporter that posts OTLP/JSON batches through the door, and closes when the door is not there. */
export class DoorExporter implements SpanExporter {
  private closed = false;
  private readonly door: Door;
  private readonly send: typeof fetch;

  constructor(door: Door, send: typeof fetch = globalThis.fetch.bind(globalThis)) {
    this.door = door;
    // Held as a bound function: the browser's fetch refuses to be called as a method of anything else.
    this.send = send;
  }

  export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
    void this.post(spans).then(resultCallback);
  }

  /** Whether a 404 from the door has closed this exporter. */
  get isClosed(): boolean {
    return this.closed;
  }

  private async post(spans: ReadableSpan[]): Promise<ExportResult> {
    if (this.closed) return { code: ExportResultCode.SUCCESS };
    const bearer = await this.door.token();
    if (bearer === null) return { code: ExportResultCode.FAILED, error: new Error('no bearer to open the door') };
    const encoded = JsonTraceSerializer.serializeRequest(spans);
    if (encoded === undefined) return { code: ExportResultCode.FAILED, error: new Error('nothing serialised') };
    const body = encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength) as ArrayBuffer;
    try {
      const response = await this.send(this.door.url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${bearer}` },
        body,
        keepalive: true,
      });
      if (response.status === 404) {
        // Tracing is Off on the api: there is nowhere to send, so stop asking.
        this.closed = true;
        return { code: ExportResultCode.SUCCESS };
      }
      return response.ok
        ? { code: ExportResultCode.SUCCESS }
        : { code: ExportResultCode.FAILED, error: new Error(`the door answered ${response.status}`) };
    } catch (error) {
      return { code: ExportResultCode.FAILED, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

/**
 * Install the provider around ONE span processor, once per page (OTel refuses a second). The app
 * calls initTracing; tests call this with a processor they can read.
 */
export function configureTracing(processor: SpanProcessor): boolean {
  if (configured) return false;
  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({ [ATTR_SERVICE_NAME]: SERVICE }),
    spanProcessors: [processor],
  });
  // register() installs the W3C propagator the fetch spans inject with, and the stack context manager.
  provider.register();
  configured = true;
  return true;
}

/** The page load: the first interaction, carrying the navigation timing, ended after the load event's paint. */
export function tracePageLoad(): Interaction {
  const load = interaction('page_load');
  pageLoadSpan = load.span;
  // Duck-typed rather than instanceof: jsdom has the entry list and not the class.
  const [navigation] = performance.getEntriesByType('navigation') as (PerformanceNavigationTiming | undefined)[];
  if (navigation && 'domainLookupStart' in navigation) {
    load.span.setAttributes({
      'polywog.timing.dns_ms': navigation.domainLookupEnd - navigation.domainLookupStart,
      'polywog.timing.connect_ms': navigation.connectEnd - navigation.connectStart,
      'polywog.timing.ttfb_ms': navigation.responseStart - navigation.requestStart,
      'polywog.timing.response_ms': navigation.responseEnd - navigation.responseStart,
      'polywog.timing.dom_content_loaded_ms': navigation.domContentLoadedEventEnd - navigation.startTime,
      'polywog.timing.transfer_bytes': navigation.transferSize,
    });
  }
  if (document.readyState === 'complete') {
    load.settle();
  } else {
    window.addEventListener('load', () => load.settle(), { once: true });
  }
  return load;
}

/**
 * A web vital as data on the trace: an event on the page-load span while it is open, else a root
 * span of its own LINKED to the page load (the vitals report late, some only when the tab hides).
 */
export function recordVital(metric: Pick<Metric, 'name' | 'value' | 'rating'>): void {
  const name = metric.name.toLowerCase();
  const value = metric.name === 'CLS' ? Math.round(metric.value * CLS_GRID) : Math.round(metric.value);
  const attributes: Attributes = { 'polywog.vital.value': value, 'polywog.vital.rating': metric.rating };
  if (current?.name === 'ui.page_load' && current.open) {
    current.span.addEvent(`web_vital.${name}`, attributes);
    return;
  }
  const links = pageLoadSpan ? [{ context: pageLoadSpan.spanContext() }] : [];
  tracer()
    .startSpan(`web_vital.${name}`, { root: true, links, attributes: { ...rootAttributes(), ...attributes } })
    .end();
}

/** The accessible name of what was clicked, as a screen reader would say it, cut to a control's length. */
function controlName(target: EventTarget | null): string {
  if (!(target instanceof Element)) return '';
  const control = target.closest('[aria-label], button, a, [role]') ?? target;
  const name = control.getAttribute('aria-label') ?? control.textContent ?? '';
  return name.trim().replace(/\s+/g, ' ').slice(0, CONTROL_NAME_MAX);
}

/** Every click is an interaction, named for its control, settled at once: it ends after the paint. */
function traceClicks(): void {
  document.addEventListener(
    'click',
    (event) => {
      const name = controlName(event.target);
      const click = interaction('click', { 'polywog.control': name });
      // Settled now: React's handlers and effects run before the second frame, so a fetch the click
      // causes begins in time to hold the span open; the paint after its response ends it.
      click.settle();
    },
    { capture: true },
  );
}

/** The interactions a navigation belongs to when one of them is open: the gesture, or the page load
 * whose first route resolution it is (the router navigates once at startup, redirects included). */
const NAVIGATING_INTERACTIONS = new Set(['ui.click', 'ui.page_load']);

/**
 * A navigation is an interaction of its own unless a click or the page load caused it, in which case
 * that interaction carries the destination and ends when the route resolves.
 */
export function traceNavigations(navigations: Navigations): void {
  navigations.subscribe('onBeforeNavigate', ({ toLocation }) => {
    if (current?.open && NAVIGATING_INTERACTIONS.has(current.name)) {
      current.span.setAttribute('polywog.navigated_to', toLocation.pathname);
      return;
    }
    interaction('navigate', { 'polywog.navigated_to': toLocation.pathname });
  });
  navigations.subscribe('onResolved', () => current?.settle());
}

/**
 * Wire the browser as a process: the door, the page load, the clicks, the navigations, the vitals.
 * Called once, as early as the app can; a second call is a no-op.
 */
export function initTracing(door: Door, navigations?: Navigations): void {
  if (!configureTracing(new BatchSpanProcessor(new DoorExporter(door)))) return;
  tracePageLoad();
  traceClicks();
  if (navigations) traceNavigations(navigations);
  onLCP(recordVital);
  onINP(recordVital);
  onCLS(recordVital);
  onFCP(recordVital);
  onTTFB(recordVital);
}

/** The open interaction, for tests and for a module that must know whether a gesture is in flight. */
export function currentInteraction(): Interaction | null {
  return current?.open ? current : null;
}
